import { labelCardTables, markWrapColumns } from './cardTableLabels';
import { isMobileNow, watchMobile } from './useIsMobile';

/* Cards whenever a table doesn't fit (user's rule, 2026-10-27).
 *
 * No text wraps in a table and nothing scrolls sideways. So a table whose one-line width is wider
 * than the space it sits in shows as cards (one card per row, "LABEL ……… value" lines), at ANY
 * screen size; a table that fits stays a normal table. On a phone every table is cards, as before.
 *
 * The decision is written to the table as data-pc-fit="cards"; assets/css/mobile.css draws the
 * cards from it (screen only, so a print always gets the table). React never sets this attribute,
 * so it never removes or fights it.
 *
 * How "doesn't fit" is measured, without flicker or flip-flopping: every candidate is laid out as a
 * TABLE and its width read, then as CARDS and that width read (a card list always takes exactly the
 * width it is given, so that is the space available). Cards when the first is wider than the second.
 * Both reads happen in the same task, before the browser paints, so neither trial layout is ever
 * seen; and the answer does not depend on which mode the table was in, so it cannot oscillate.
 *
 * 2026-10-28: cards now only on a PHONE. At 768px and wider (tablets, laptops) no table is ever
 * cards: one that doesn't fit is scaled down to fit instead (decideCards), like the reports.
 *
 * Runs when tables appear or change (MutationObserver), when the window or the content area
 * changes width (sidebar opened / closed), and once fonts have loaded. It also puts the column
 * names on the cells (helper/cardTableLabels.js), so a card is never shown unlabelled.
 */
const CANDIDATES = 'table.pc-card-table, table.pc-swipe-table, table.pc-grid-table';
const ATTR = 'data-pc-fit';
// Account Balance Detail (T1.50): always a table, and its text wraps.
const REF_TABLES = 'table.pc-ref-table';
/* Reports and Weekly Sale's three detail pop-ups (user, 2026-10-28): always TABLE rows, never cards.
 * When one is wider than its space it is scaled down to fit (CSS zoom), as much as needed and no
 * more — a narrow table keeps full size — but never below MIN_ZOOM (~6px text). On a phone its text
 * may wrap at spaces (mobile.css), which narrows it before any scaling; on desktop it stays on one
 * line (the no-wrap rule). Print always resets the zoom (index.css). */
const SHRINK_TABLES = 'table.pc-shrink-table';
const MIN_ZOOM = 0.45;
// Tables whose text may wrap, so their hyphenated words are kept whole.
const WRAPPING = `${REF_TABLES}, ${SHRINK_TABLES}`;

/* Hyphenated words stay whole (user, 2026-10-28): text that wraps — a card, or the Account Balance
 * table — must never split "VD-CCo12" after its hyphen, and CSS has no switch for that. So inside
 * those tables a hyphen between two non-spaces becomes U+2011, the NON-BREAKING hyphen, which looks
 * the same. Tables shown as ordinary one-line tables get the plain hyphen back, so the browser's
 * Find still matches there; copying always gives plain hyphens (see startFitCards). Only a
 * text node's value is changed — never the DOM structure — so React is unaffected: when it next
 * renders that text it writes its own value, and the next pass swaps the hyphen again. */
const NB_HYPHEN = '\u2011';
// No lookbehind: Safari before 16.4 cannot parse one, and the whole bundle would fail to load.
const WORD_HYPHEN = /(\S)-(?=\S)/g;
const NB_ANY = /\u2011/g;

function setHyphens(table, keepWhole) {
    const walker = document.createTreeWalker(table, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        const v = n.nodeValue;
        // A free-text cell that may wrap (data-pc-wrap) always keeps hyphenated words whole. Decided
        // per node in one pass, so a run never undoes what the same run did (which would loop).
        const whole = keepWhole || !!(n.parentElement && n.parentElement.closest('[data-pc-wrap]'));
        const next = whole ? v.replace(WORD_HYPHEN, `$1${NB_HYPHEN}`) : v.replace(NB_ANY, '-');
        if (next !== v) n.nodeValue = next;
    }
}

function setCards(table, on) {
    if (on) { if (table.getAttribute(ATTR) !== 'cards') table.setAttribute(ATTR, 'cards'); }
    else if (table.hasAttribute(ATTR)) table.removeAttribute(ATTR);
}

/* Scroll positions that the trial layouts could disturb (a shorter table layout clamps them). */
function saveScroll(tables) {
    const seen = new Set();
    const saved = [];
    const keep = (el) => {
        if (!el || seen.has(el)) return;
        seen.add(el);
        if (el.scrollTop || el.scrollLeft) saved.push([el, el.scrollTop, el.scrollLeft]);
    };
    keep(document.scrollingElement || document.documentElement);
    tables.forEach((t) => { for (let el = t.parentElement; el && el !== document.body; el = el.parentElement) keep(el); });
    return () => saved.forEach(([el, top, left]) => {
        if (el.scrollTop !== top) el.scrollTop = top;
        if (el.scrollLeft !== left) el.scrollLeft = left;
    });
}

export function fitCardTables(root = document) {
    labelCardTables(root);
    root.querySelectorAll(`${CANDIDATES}, ${WRAPPING}`).forEach(markWrapColumns);
    const tables = [...root.querySelectorAll(CANDIDATES)];
    decideCards(tables);
    tables.forEach((t) => setHyphens(t, t.getAttribute(ATTR) === 'cards'));
    root.querySelectorAll(WRAPPING).forEach((t) => setHyphens(t, true));
    // Account Balance Detail too: its text already scales with its box, and with whole amounts
    // (2026-10-28) a full 9-shop block on a phone may still need a little zoom on top.
    shrinkToFit([...root.querySelectorAll(`${SHRINK_TABLES}, ${REF_TABLES}`)]);
}

/* Each table's narrowest layout (min-content: one line on desktop, words whole on a phone) against
 * the width of the box it sits in; zoomed by the ratio when it is wider. Measured unzoomed, so the
 * answer never depends on the zoom already applied. All writes, then all reads, so it costs two
 * layouts however many tables there are. */
function shrinkToFit(tables) {
    const shown = tables.filter((t) => t.parentElement);
    if (shown.length === 0) return;
    shown.forEach((t) => { t.style.zoom = ''; t.style.width = 'min-content'; });
    const need = shown.map((t) => t.getBoundingClientRect().width);
    const room = shown.map((t) => {
        const box = t.parentElement;
        const cs = getComputedStyle(box);
        return box.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
    });
    shown.forEach((t, i) => {
        t.style.width = '';
        if (!need[i] || !room[i]) return;            // hidden (a closed tab / pop-up)
        const z = room[i] / need[i];
        if (z < 0.995) t.style.zoom = String(Math.max(MIN_ZOOM, Math.floor(z * 1000) / 1000));
    });
}

/* Phone: every candidate is cards. Wider screens (user, 2026-10-28): NEVER cards — lists and
 * entry-form tables stay tables, and one wider than its space is scaled down to fit (CSS zoom),
 * exactly as much as needed, never below MIN_ZOOM. The space is measured the way the card
 * decision always was: laid out as cards, a table takes exactly the width it is given (which a
 * table, whose minimum is its content, would not — a flex panel or PhoneClinicGrid's fit-content
 * card would grow with it). */
function decideCards(tables) {
    if (tables.length === 0) return;
    if (isMobileNow()) { tables.forEach((t) => { t.style.zoom = ''; setCards(t, true); }); return; }

    const restore = saveScroll(tables);
    tables.forEach((t) => { t.style.zoom = ''; setCards(t, false); });
    // As a table, unscaled: its own width, or more if anything inside spills past it.
    const need = tables.map((t) => Math.max(t.getBoundingClientRect().width, t.scrollWidth));
    tables.forEach((t) => setCards(t, true));
    // As cards: the width it is given.
    const room = tables.map((t) => t.getBoundingClientRect().width);
    tables.forEach((t, i) => {
        setCards(t, false);
        // A hidden table (0 wide) is left unscaled; it is measured again when it changes.
        if (room[i] > 0 && need[i] > room[i] + 1) {
            t.style.zoom = String(Math.max(MIN_ZOOM, Math.floor((room[i] / need[i]) * 1000) / 1000));
        }
    });
    restore();
}

/* Is this mutation about a table (rows loaded, a value changed, a table or pop-up added)? */
function touchesTables(records) {
    for (const r of records) {
        const el = r.target.nodeType === 1 ? r.target : r.target.parentElement;
        if (!el) continue;
        if (el.closest(CANDIDATES) || el.closest(WRAPPING)) return true;
        if (r.type === 'childList') {
            for (const n of r.addedNodes) {
                if (n.nodeType === 1 && (n.matches(CANDIDATES) || n.querySelector(CANDIDATES)
                    || n.matches(WRAPPING) || n.querySelector(WRAPPING))) return true;
            }
            // Something next to a table came or went (a side panel): its space may have changed.
            if (r.removedNodes.length && (el.querySelector(CANDIDATES) || el.querySelector(WRAPPING))) return true;
        }
    }
    return false;
}

/* Starts the checks for everything under root; returns the stop function. */
export function startFitCards(root = document.body) {
    if (!root || typeof MutationObserver === 'undefined') return () => {};
    const run = () => fitCardTables(root);
    // After a resize or a layout switch, run now and once more when React has re-rendered.
    let later = null;
    const runTwice = () => {
        run();
        if (later !== null) clearTimeout(later);
        later = setTimeout(() => { later = null; run(); }, 60);
    };

    // In the observer callback itself: that is before the browser paints, so a table that does not
    // fit is never drawn overflowing first. Attributes are not observed, so our own changes
    // (data-pc-fit, data-label) can't retrigger it.
    const observer = new MutationObserver((records) => { if (touchesTables(records)) run(); });
    observer.observe(root, { childList: true, subtree: true, characterData: true });

    window.addEventListener('resize', runTwice);
    const unwatchMobile = watchMobile(runTwice);

    // The content area changes width without a window resize when the sidebar opens or closes.
    // Its WIDTH only: on a phone its height follows the page, which our own changes alter. Acted on
    // in a timer, never inside the observer's callback (that would be a "ResizeObserver loop").
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
        let lastWidth = null;
        ro = new ResizeObserver((entries) => {
            const w = Math.round(entries[entries.length - 1].contentRect.width);
            if (w === lastWidth) return;
            lastWidth = w;
            setTimeout(run, 0);
        });
        const content = root.querySelector('.pc-admin .content');
        if (content) ro.observe(content);
    }

    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run).catch(() => {});

    // Copying from a table gives the plain hyphen back ("VD-CCo12"), so a pasted code still
    // matches in a search box.
    const onCopy = (e) => {
        const text = window.getSelection ? String(window.getSelection()) : '';
        if (!text.includes(NB_HYPHEN) || !e.clipboardData) return;
        e.clipboardData.setData('text/plain', text.replace(NB_ANY, '-'));
        e.preventDefault();
    };
    document.addEventListener('copy', onCopy);
    run();

    return () => {
        document.removeEventListener('copy', onCopy);
        observer.disconnect();
        window.removeEventListener('resize', runTwice);
        unwatchMobile();
        if (ro) ro.disconnect();
        if (later !== null) clearTimeout(later);
    };
}
